import { request } from '@/api/index';
import { LeadByEmailRes } from './interface/lead';
import { getTimestamp } from '@/api/common';
import {
  LeadResquest,
  LeadResponse,
  SendPhoneCode,
  PosterBuryingPointReq,
  PosterBuryingPointRes,
  ExpandSubjectsLabelReq,
  ExpandSubjectsLabelRes,
  StudentInformationRes,
  StudentInformationReq,
  EmailVerificationCodeRequest,
  EmailVerificationCodeResponse,
  ChannelResponse,
  StudentCodeReq,
  StudentInformationBaseReq,
  StudentInformationBaseRes,
  SendVoiceCode,
  VoiceVerificationCodeRequest,
  IGradeDateReq,
  IGradeDateRes,
  TrackBaseInfoRes,
  GetStudentInfoRes,
  StudentTrialClassScheduleRes,
} from './interface/lead';

// 新增：V2短信验证码接口参数
export interface SendSmsCodeV2 {
  trackId?: string; // 线索ID
  studentCode?: string; // 学号
}

// 新增：V2验证码校验接口参数
export interface VerifySmsCodeV2 {
  trackId?: string; // 线索ID
  studentCode?: string; // 学号
  code: string; // 验证码
}

// 新增：V2语音验证码校验接口参数
export interface VerifyVoiceCodeV2 {
  trackId?: string; // 线索ID
  studentCode?: string; // 学号
  verificationCode: string; // 验证码
}

// 创建线索
export const createLead = (params: LeadResquest) => {
  return request.post<LeadResponse, LeadResponse>('/public/lead', params, {
    needAuth: true,
    needSid: true,
    sidName: 'x-lp-sid',
  });
};

// 海报事件埋点
export const sendPosterBuryingPoint = (params: PosterBuryingPointReq) => {
  return request.post<PosterBuryingPointRes, PosterBuryingPointRes>(`/public/poster/center/rio/lead/data`, {
    ...params,
  });
};

// 扩科打标签接口
export const expandSubjectsLabel = (params: ExpandSubjectsLabelReq) => {
  return request.post<ExpandSubjectsLabelRes, ExpandSubjectsLabelRes>(`/public/lead/intention`, {
    ...params,
  });
};
// 扩科打标签接口 不提醒
export const expandSubjectsLabelNoRemind = (params: ExpandSubjectsLabelReq) => {
  return request.post<ExpandSubjectsLabelRes, ExpandSubjectsLabelRes>(
    `/public/lead/intention`,
    {
      ...params,
    },
    { skipErrorHandler: true },
  );
};

// 通过学号查询学生信息
export const getStudentInformation = (params: StudentInformationReq) => {
  return request.post<StudentInformationRes, StudentInformationRes>(`/public/student/baseInfo/${params.code}`);
};

// 通过学号和/或trackId获取学生信息（脱敏版本）
export const getStudentInfo = (params: { trackId?: string; studentCode?: string; subject?: string }) => {
  const queryParts: string[] = [];
  if (params.trackId) queryParts.push(`trackId=${params.trackId}`);
  if (params.studentCode) queryParts.push(`studentCode=${params.studentCode}`);
  if (params.subject) queryParts.push(`subject=${params.subject}`);
  return request
    .get<GetStudentInfoRes, GetStudentInfoRes>(`/public/getTrackInfo?${queryParts.join('&')}`)
    .then((res) => {
      return res.content;
    });
};

// 通过学号查询学生信息
export const getStudentInformationBase = (params: StudentInformationBaseReq) => {
  return request.post<StudentInformationBaseRes, StudentInformationBaseRes>(`/public/track/baseInfo`, params);
};

// 通过trackId查询学生信息
export const getStudentBaseInfoByTrackId = (trackId: string) => {
  return request.get(`/public/${trackId}/baseInfo`);
};

// 发送短信验证码
export const sendPhoneVerificationCode = (params: SendPhoneCode) => {
  return getTimestamp().then((res) => {
    return request.put('/public/sms/send', params, {
      signature: true,
      timestamp: res,
      needSid: true,
    });
  });
};

/**
 * 发送短信验证码（安全）
 * 2025-04-17 新增 , 使用统一 useCaptcha 组件进行的安全验证
 */
export const sendPhoneVerificationCodeSecured = (params: SendPhoneCode, headers: any) => {
  return getTimestamp().then((res) => {
    return request.put('/public/sms/send/secured', params, {
      signature: true,
      timestamp: res,
      signatureHeaders: headers,
    });
  });
};

/**
 * 发送短信验证码V2（数据安全版本）
 * 使用trackId和studentCode代替手机号
 */
export const sendPhoneVerificationCodeV2 = (params: SendSmsCodeV2, headers: any) => {
  return getTimestamp().then((res) => {
    return request.put('/public/sms/v2/send/secured', params, {
      signature: true,
      timestamp: res,
      signatureHeaders: headers,
    });
  });
};

/**
 * 发送语音验证码V2（数据安全版本）
 * 使用trackId和studentCode代替手机号
 */
export const sendVoiceVerificationCodeV2 = (params: SendSmsCodeV2, headers: any) => {
  return getTimestamp().then((res) => {
    return request.put('/public/sms/v2/send/voice/secured', params, {
      signature: true,
      timestamp: res,
      signatureHeaders: headers,
    });
  });
};

/**
 * 验证短信验证码V2（数据安全版本）
 * 使用trackId和studentCode代替手机号
 */
export const verifyPhoneVerificationCodeV2 = (data: VerifySmsCodeV2) => {
  return request.post<EmailVerificationCodeResponse, EmailVerificationCodeResponse>(
    '/public/sms/v2/verification',
    data,
  );
};

/**
 * 验证语音验证码V2（数据安全版本）
 * 使用trackId和studentCode代替手机号
 */
export const verifyVoiceVerificationCodeV2 = (data: VerifyVoiceCodeV2) => {
  return request.post<EmailVerificationCodeResponse, EmailVerificationCodeResponse>(
    '/public/sms/v2/verification/voice',
    data,
  );
};

// 验证短信验证码
export const verifyPhoneVerificationCode = (data: EmailVerificationCodeRequest) => {
  return request.post<EmailVerificationCodeResponse, EmailVerificationCodeResponse>('/public/sms/verification', data);
};

// 发送语音验证码
export const sendVoiceVerificationCode = (params: SendVoiceCode) => {
  return getTimestamp().then((res) => {
    return request.put('/public/sms/send/voice', params, {
      signature: true,
      timestamp: res,
      needSid: true,
    });
  });
};

/**
 * 发送语音验证码（安全）
 * 2025-04-17 新增 , 使用统一 useCaptcha 组件进行的安全验证
 */
export const sendVoiceVerificationCodeSecured = (params: SendVoiceCode, headers: any) => {
  return getTimestamp().then((res) => {
    return request.put('/public/sms/send/voice/secured', params, {
      signature: true,
      timestamp: res,
      signatureHeaders: headers,
    });
  });
};

// 验证语音验证码
export const verifyVoiceVerificationCode = (data: VoiceVerificationCodeRequest) => {
  return request.post<EmailVerificationCodeResponse, EmailVerificationCodeResponse>(
    '/public/sms/verification/voice',
    data,
  );
};

export const getJsonNode = (id: string) => {
  return request.get<ChannelResponse, ChannelResponse>(`/public/ref/json_node/${id}`);
};

// 约课获取学生学号
export const getOldUserStudentCode = (params: StudentCodeReq) => {
  return request.post<any, any>('/public/poster/center/track/info/data/', params);
};

// 获取年级
export const getGradeDate = (params: IGradeDateReq) => {
  return request.post<IGradeDateRes, IGradeDateRes>('/public/ref/grade-year/search', params);
};

// 查询是否有约课记录
export const haveAppointmentRecord = (id: string) => {
  return request.get<TrackBaseInfoRes, TrackBaseInfoRes>(`/public/reserve/canReserveTrailClass?trackId=${id}`);
};

/** 根据 trackId 查询线索体验课排课状态 */
export const findStudentTrialClassSchedule = (trackId: string) => {
  return request.get<StudentTrialClassScheduleRes, StudentTrialClassScheduleRes>(
    `/public/reserve/findStudentTrialClassSchedule?trackId=${trackId}`,
  );
};

/**
 * 根据线索ID获取当前科目的推荐拓科信息
 * @param trackId
 * @param lang
 * @returns
 */
export const recommendedSubjects = (trackId: string, lang: string, preSubjectReservedSuccess: boolean) => {
  return request
    .get<any, any>(
      `/public/recommended-subjects/${trackId}?lang=${lang}&preSubjectReservedSuccess=${preSubjectReservedSuccess}`,
    )
    .then((res) => res.content);
};

/**
 * 一屏落地页拓科
 * @param params
 * @returns
 */
export const landingOneIntention = (params: any) => {
  return request.post(`/public/lead/landingOne/intention`, params);
};

/**
 * 一屏落地页约课-数据安全版本
 * @param params
 * @returns
 */
export const landingOneReserve = (params: any, headers?: any) => {
  return request.post(`/public/reserve/v3/reserve/mobile`, params, {
    headers,
  });
};

/**
 * 结果页获取体验课列表详情
 * @param studentCode
 * @returns
 */
export const trailClassList = (params: any) => {
  return request.post<any, any>(`/public/course/trailClass/list`, params).then((res) => res.content);
};

// Facebook 根据邮箱查询线索（已废弃，使用 getLeadByFbToken 代替）
export const getLeadByEmail = (email: string, subject: string) => {
  const url = `/public/facebook/lead/by-email?email=${encodeURIComponent(email)}&subject=${encodeURIComponent(
    subject,
  )}`;
  return request.get<LeadByEmailRes, LeadByEmailRes>(url);
};

// Facebook 根据 access token 查询线索
export const getLeadByFbToken = (accessToken: string, subject: string) => {
  const url = `/public/facebook/lead/by-fb-token?accessToken=${encodeURIComponent(
    accessToken,
  )}&subject=${encodeURIComponent(subject)}`;
  return request.get<LeadByEmailRes, LeadByEmailRes>(url);
};
